import React, { FunctionComponent, useCallback } from "react";

import { useThrottledCallback } from "use-debounce";
import useMap from "@/@alpaca-travel/react-mapbox-gl/use-map";
import useMapEvent from "@/@alpaca-travel/react-mapbox-gl/use-map-event";
import useIndicationEffects from "@/use-indication-effects";
import useIndication from "@/use-indication";
import MapMarker from "@/@alpaca-travel/react-mapbox-gl/map-marker";
import useSelectionEffects from "@/use-selection-effects";
import MapPopup from "@/@alpaca-travel/react-mapbox-gl/map-popup";
import Card from "../page/browse/card";
import useMapDimensions from "./use-map-dimensions";
import { MIN_WIDTH_FOR_PADDING_LEFT_APPLY } from "@/types";
import useSelection from "@/use-selection";
import useAmpolRefinedServices from "@/use-refined-ampol-services";
import mapboxgl, { PointLike } from "mapbox-gl";

const Interactions: FunctionComponent<InteractionProps> = () => {
  const { map } = useMap();

  const { setIndication } = useIndicationEffects();
  const { setSelection } = useSelectionEffects();
  const { ampolServices: indicatedAmpolServices } = useIndication();
  const { dimensions } = useMapDimensions("alpacaAmpolMap");
  const { ampolService } = useSelection();

  const { position, ampolServices: refinedAmpolServices } =
    useAmpolRefinedServices();

  const onClickHandler = useCallback(
    (mapboxEvent: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
      if (map == null || map.getLayer("ampol-services") == null) {
        return;
      }

      // Create a wider geometry for selecting
      const width = 50;
      const height = 50;
      const { point } = mapboxEvent;
      const geometry: [PointLike, PointLike] = [
        [point.x - width / 2, point.y],
        [point.x + width / 2, point.y + height],
      ];

      // Query the touch..
      const features = map.queryRenderedFeatures(geometry, {
        layers: ["ampol-services"],
      });

      const $ids = features
        .map((feature) => feature.properties?.$id)
        .filter((f) => f != null);

      setSelection($ids[0] ?? null);
      setIndication(null);
    },
    [map, setSelection, setIndication]
  );

  const onMouseMoveHandler = useCallback(
    (mapboxEvent: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) => {
      if (map == null || map.getLayer("ampol-services") == null) {
        return;
      }

      // Disable hovering with touch devices
      if (
        "ontouchstart" in window &&
        dimensions.width < MIN_WIDTH_FOR_PADDING_LEFT_APPLY
      ) {
        return;
      }

      // Create a wider geometry for selecting
      const width = 30;
      const height = 50;
      const { point } = mapboxEvent;
      const geometry: [PointLike, PointLike] = [
        [point.x - width / 2, point.y],
        [point.x + width / 2, point.y + height],
      ];

      // Query the touch..
      const features = map.queryRenderedFeatures(geometry, {
        layers: ["ampol-services"],
      });

      const $ids = features
        .map((feature) => feature.properties?.$id)
        .filter((f) => f != null);

      setIndication((prevValue) => {
        // If we have a interaction set, but it was not from the map..
        if (prevValue != null && prevValue.point == null) {
          return prevValue;
        }

        return $ids.length > 0 ? { $ids, point: mapboxEvent.point } : null;
      });
    },
    [map, setIndication, dimensions]
  );
  const onMouseMoveHandlerThrottled = useThrottledCallback(
    onMouseMoveHandler,
    100
  );

  useMapEvent("click", onClickHandler);
  useMapEvent("mousemove", onMouseMoveHandlerThrottled);

  const indicatedAmpolService = indicatedAmpolServices?.[0];

  return (
    <>
      {position != null && (
        <MapMarker
          color="#F00"
          lngLat={{
            lng: position[0],
            lat: position[1],
          }}
        />
      )}
      {refinedAmpolServices != null &&
        refinedAmpolServices.slice(0, 5).map((ampolService) => {
          const isIndicated =
            indicatedAmpolService &&
            indicatedAmpolService.$id === ampolService.$id;
          if (isIndicated) {
            return null;
          }
          return (
            <MapMarker
              key={ampolService.$id}
              lngLat={{
                lng: ampolService.position[0],
                lat: ampolService.position[1],
              }}
              offset={[-1.5, -19]}
            >
              <img
                className={`tw-w-16 tw-p-2`}
                style={{ marginTop: 0, marginBottom: 0 }}
                src="https://cdn.alpacamaps.com/nrma/ampol-member-benefits/Ampol_Pin_MyNRMA.png"
                alt={`${ampolService.name} Map Marker`}
                onClick={(e) => {
                  e.preventDefault();
                  setSelection(ampolService.$id);
                }}
              />
            </MapMarker>
          );
        })}
      {ampolService != null && (
        <MapMarker
          lngLat={{
            lng: ampolService.position[0],
            lat: ampolService.position[1],
          }}
          offset={[-1.5, -25]}
        >
          <img
            className={`tw-w-20 tw-p-2`}
            style={{ marginTop: 0, marginBottom: 0 }}
            src="https://cdn.alpacamaps.com/nrma/ampol-member-benefits/Ampol_Pin_MyNRMA.png"
            alt={`${ampolService.name} Map Marker`}
            onClick={(e) => {
              e.preventDefault();
              setSelection(ampolService.$id);
            }}
          />
        </MapMarker>
      )}

      {indicatedAmpolService != null &&
        (ampolService == null ||
          ampolService.$id !== indicatedAmpolService.$id) &&
        dimensions.width > MIN_WIDTH_FOR_PADDING_LEFT_APPLY && (
          <>
            <MapMarker
              lngLat={{
                lng: indicatedAmpolService.position[0],
                lat: indicatedAmpolService.position[1],
              }}
              offset={[-1.5, -25]}
            >
              <img
                className={`tw-w-20 tw-p-2`}
                style={{ marginTop: 0, marginBottom: 0 }}
                src="https://cdn.alpacamaps.com/nrma/ampol-member-benefits/Ampol_Pin_MyNRMA.png"
                alt={`${indicatedAmpolService.name} Map Marker`}
                onClick={(e) => {
                  e.preventDefault();
                  setSelection(indicatedAmpolService.$id);
                }}
              />
            </MapMarker>
            <MapPopup
              lngLat={{
                lng: indicatedAmpolService.position[0],
                lat: indicatedAmpolService.position[1],
              }}
              offset={[0, -50]}
            >
              <Card
                service={indicatedAmpolService}
                onClick={() => setSelection(indicatedAmpolService.$id)}
              />
            </MapPopup>
          </>
        )}
    </>
  );
};

export type InteractionProps = {};

export default React.memo(Interactions);
